import { Box, Paper, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { ChartDataset, Point } from "chart.js";
import "chart.js/auto";
import { useMemo } from "react";
import { Line } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { useCurrency } from "../../context/CurrencyContext.tsx";
import { useTheme } from "../../context/ThemeContext";
import { OutcomeSummary } from "../../shared/Analytics";
import { getTooltip } from "../../utils/chartjsTheming";
import { SectionLabel } from "../general/SectionLabel";
import {
  RISK_CHART_HEIGHT,
  RISK_LINE_POINTS,
  riskChartLegend,
  riskChartScales,
} from "./riskChart";

/** How much of its colour the area under the expenses takes. */
const EXPENSES_AREA_ALPHA = 0.28;

export const LawFirmRiskAnalysis = ({
  outcomes,
}: {
  outcomes: OutcomeSummary[];
}) => {
  const { theme } = useTheme();
  const { currency } = useCurrency();
  const { t } = useTranslation(undefined, {
    keyPrefix: "lawFirmRiskAnalysis",
  });
  const { positive, negative } = theme.palette.data;

  const {
    incomeDataset,
    expensesDataset,
  }: {
    incomeDataset: ChartDataset<"line", Point[]>;
    expensesDataset: ChartDataset<"line", Point[]>;
  } = useMemo(() => {
    const incomeDataset: ChartDataset<"line", Point[]> = {
      label: t("income"),
      data: [],
      stepped: true,
      fill: false,
      // a line has no area: its box in the legend stays empty
      backgroundColor: "transparent",
      borderColor: positive.main,
      borderWidth: 2.5,
      ...RISK_LINE_POINTS,
      order: 2,
    };
    const expensesDataset: ChartDataset<"line", Point[]> = {
      label: t("expense"),
      data: [],
      stepped: true,
      fill: true,
      backgroundColor: alpha(negative.main, EXPENSES_AREA_ALPHA),
      borderColor: negative.main,
      borderWidth: 2,
      ...RISK_LINE_POINTS,
      order: 1,
    };
    const lawFirmOutcomes: {
      probability: number;
      income: number;
      expenses: number;
    }[] = outcomes
      // a firm whose costs are unknown has no expenses to draw
      .flatMap(({ probability, lawFirm }) =>
        lawFirm.costs === "known"
          ? [{ probability, expenses: lawFirm.expenses, income: lawFirm.income }]
          : [],
      )
      .sort((o1, o2) => o1.income - o1.expenses - (o2.income - o2.expenses));

    let cumulativeProbability = 0;
    for (const { probability, expenses, income } of lawFirmOutcomes) {
      // push income and expense at current cumulative probability
      incomeDataset.data.push({
        x: cumulativeProbability,
        y: income,
      });
      expensesDataset.data.push({
        x: cumulativeProbability,
        y: expenses,
      });
      cumulativeProbability += probability;
    }
    // close data at 100%
    const last = lawFirmOutcomes[lawFirmOutcomes.length - 1];
    if (last !== undefined) {
      incomeDataset.data.push({ x: 1, y: last.income });
      expensesDataset.data.push({ x: 1, y: last.expenses });
    }

    return { incomeDataset, expensesDataset };
  }, [outcomes, t, positive, negative]);

  if (!outcomes.length) {
    return (
      <Typography sx={{ color: "text.secondary" }}>
        {t("noOutcomes")}
      </Typography>
    );
  }

  const datasets: ChartDataset<"line", Point[]>[] = [];
  const incomeDatasetIndex = datasets.push(incomeDataset) - 1;
  const expensesDatasetIndex = datasets.push(expensesDataset) - 1;

  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, px: 2.5, pt: 2, pb: 2 }}>
      <SectionLabel component="h2">{t("title")}</SectionLabel>
      <Box sx={{ height: RISK_CHART_HEIGHT }}>
        <Line
          data={{ datasets }}
          options={{
            maintainAspectRatio: false,
            plugins: {
              legend: riskChartLegend(theme),
              tooltip: {
                ...getTooltip(theme),
                mode: "index",
                axis: "x",
                intersect: false,
                filter: ({ chart, datasetIndex, dataIndex, parsed: { x } }) => {
                  const series = chart.data.datasets[
                    datasetIndex
                  ] as ChartDataset<"line", Point[]>;
                  const firstIndexAtX = series.data.findIndex(
                    (pt) => pt.x === x,
                  );
                  return dataIndex === firstIndexAtX;
                },
                callbacks: {
                  title: () => "",
                  label: ({ dataset, parsed }) =>
                    `${dataset.label}: ${currency.format(parsed.y ?? 0)}`,
                  // profit/loss = income – expenses
                  footer: (items) => {
                    const incomeItem = items.find(
                      ({ datasetIndex }) => datasetIndex === incomeDatasetIndex,
                    );
                    if (incomeItem === undefined) return "";

                    const expensesItem = items.find(
                      ({ datasetIndex }) =>
                        datasetIndex === expensesDatasetIndex,
                    );
                    if (expensesItem === undefined) return "";

                    const netResult =
                      (incomeItem.parsed.y ?? 0) - (expensesItem.parsed.y ?? 0);
                    return t(netResult < 0 ? "loss" : "profit", {
                      value: currency.format(netResult),
                    });
                  },
                },
              },
            },
            scales: riskChartScales({
              theme,
              currency,
              axisTitle: t("axis"),
              y: { beginAtZero: true },
            }),
          }}
        />
      </Box>
    </Paper>
  );
};
